import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from '@Helpers';

import Badge from '@Components/Badge/Badge';
import { Button } from '@Components/ui-v2/button';
import { FieldServiceContract, FieldServiceListItem, Product, Store, TaskType } from '@Models';
import { bopsApi } from '@Network';
import { getErrorDetail } from '../constants';
import WizardStepper from './components/wizardStepper';
import Step1Where from './steps/step1Where';
import Step2What from './steps/step2What';
import Step3Frequency from './steps/step3Frequency';
import Step4Firm from './steps/step4Firm';
import {
  buildScheduleCreatePayload,
  buildScheduleName,
  computeFieldServiceCoverage,
  countFieldServicesWithCoverage,
  countFullyEligibleFieldServices,
  DEFAULT_WIZARD_FORM_STATE,
  EMPTY_FIELD_SERVICE_COVERAGE,
  FieldServiceCoverage,
  getCategoryOptions,
  getLockedRetailer,
  getSelectedRetailers,
  getStoreKey,
  getTaskTypesMissingCategories,
  hasFieldServiceCoverage,
  hasNoSharedTaskType,
  isStoreCoveredByFieldService,
  listFullyEligibleFieldServiceIds,
  WIZARD_STEPS,
} from './constants';
import { ScheduleWizardWhereState } from './scheduleWizard.types';

interface ScheduleWizardProps {
  stores: Store[];
  products: Product[];
  taskTypes: TaskType[];
  fieldServiceContracts: FieldServiceContract[];
  fieldServices: FieldServiceListItem[];
}

type EligibilityState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; coverage: FieldServiceCoverage };

const ELIGIBILITY_DEBOUNCE_MS = 300;

const ScheduleWizard = ({ stores, products, taskTypes, fieldServiceContracts, fieldServices }: ScheduleWizardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [formState, setFormState] = useState(DEFAULT_WIZARD_FORM_STATE);

  const currentStep = WIZARD_STEPS[stepIndex];
  const isLastStep = stepIndex === WIZARD_STEPS.length - 1;
  const taskTypesMissingCategories = useMemo(
    () => getTaskTypesMissingCategories(formState, taskTypes, products),
    [formState, taskTypes, products],
  );
  const canAdvance =
    currentStep.isValid(formState) && (currentStep.key !== 'what' || taskTypesMissingCategories.length === 0);

  const [eligibility, setEligibility] = useState<EligibilityState>({ status: 'loading' });
  const { where } = formState;
  const selectedTaskTypeIds = formState.what.taskTypeIds;

  useEffect(() => {
    let cancelled = false;
    const retailers = getSelectedRetailers(where);

    if (retailers.length === 0) {
      setEligibility({ status: 'ready', coverage: computeFieldServiceCoverage([], []) });
      return undefined;
    }

    setEligibility({ status: 'loading' });

    const fetchEligibility = async () => {
      try {
        const perRetailerResults = await Promise.all(
          retailers.map(async (retailer) => ({
            retailer,
            contracts: await bopsApi.getFieldServiceEligibility({
              retailer,
              location_codes: where.selectedStores
                .filter((store) => store.retailer === retailer)
                .map((store) => store.location_code),
              task_type_ids: selectedTaskTypeIds,
            }),
          })),
        );
        if (cancelled) return;
        setEligibility({
          status: 'ready',
          coverage: computeFieldServiceCoverage(where.selectedStores, perRetailerResults),
        });
      } catch (error) {
        if (!cancelled) setEligibility({ status: 'error' });
      }
    };

    const timeoutId = window.setTimeout(() => {
      fetchEligibility().catch(() => {});
    }, ELIGIBILITY_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [where, selectedTaskTypeIds]);

  useEffect(() => {
    if (eligibility.status !== 'ready') return;
    const { coverage } = eligibility;
    setFormState((prev) => {
      const nextSingleId =
        prev.firm.singleFieldServiceId && hasFieldServiceCoverage(coverage, prev.firm.singleFieldServiceId)
          ? prev.firm.singleFieldServiceId
          : null;
      const nextStoreAssignments = Object.fromEntries(
        Object.entries(prev.firm.storeAssignments).filter(([storeKey, fieldServiceId]) =>
          isStoreCoveredByFieldService(coverage, storeKey, fieldServiceId),
        ),
      );
      const unchanged =
        nextSingleId === prev.firm.singleFieldServiceId &&
        Object.keys(nextStoreAssignments).length === Object.keys(prev.firm.storeAssignments).length;
      if (unchanged) return prev;
      return {
        ...prev,
        firm: { ...prev.firm, singleFieldServiceId: nextSingleId, storeAssignments: nextStoreAssignments },
      };
    });
  }, [eligibility]);

  const isLoadingEligibility = eligibility.status === 'loading';
  const hasEligibilityError = eligibility.status === 'error';
  const fieldServiceCoverage = eligibility.status === 'ready' ? eligibility.coverage : EMPTY_FIELD_SERVICE_COVERAGE;
  const lacksSharedTaskType = hasNoSharedTaskType(formState, fieldServiceContracts);
  const totalFieldServicesWithCoverage = countFieldServicesWithCoverage(fieldServiceCoverage);
  const fullyEligibleFieldServiceCount = countFullyEligibleFieldServices(fieldServiceCoverage);
  const eligibilityBadgeVariant = isLoadingEligibility
    ? 'gray'
    : hasEligibilityError || totalFieldServicesWithCoverage === 0
    ? 'red'
    : fullyEligibleFieldServiceCount === totalFieldServicesWithCoverage
    ? 'green'
    : 'yellow';
  const eligibleFieldServices = useMemo(() => {
    const seenIds = new Set<string>();
    return fieldServices.filter((fieldService) => {
      if (!hasFieldServiceCoverage(fieldServiceCoverage, fieldService.id) || seenIds.has(fieldService.id)) {
        return false;
      }
      seenIds.add(fieldService.id);
      return true;
    });
  }, [fieldServices, fieldServiceCoverage]);

  useEffect(() => {
    const fullyEligibleIds = listFullyEligibleFieldServiceIds(fieldServiceCoverage);
    if (fullyEligibleIds.length !== 1) return;
    const onlyId = fullyEligibleIds[0];
    setFormState((prev) => {
      if (prev.firm.mode === 'single' && prev.firm.singleFieldServiceId === onlyId) return prev;
      return { ...prev, firm: { ...prev.firm, mode: 'single', singleFieldServiceId: onlyId } };
    });
  }, [fieldServiceCoverage]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildScheduleCreatePayload(formState, products, fieldServiceContracts, fieldServiceCoverage);
      await bopsApi.postSchedule(payload);
      showSuccessToast(t('Schedule.Wizard.CreateSuccess'));
      navigate('/schedules');
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Wizard.CreateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [stepIndex]);

  const goBack = () => {
    if (stepIndex === 0) {
      navigate('/schedules');
      return;
    }
    setStepIndex((prev) => prev - 1);
  };

  const goNext = () => {
    if (!canAdvance || isLastStep) return;
    if (WIZARD_STEPS[stepIndex + 1].key === 'firm') {
      setFormState((prev) =>
        prev.name.trim() === '' ? { ...prev, name: buildScheduleName(prev.what, taskTypes, t) } : prev,
      );
    }
    setStepIndex((prev) => prev + 1);
  };

  const goToStep = (index: number) => {
    if (index < stepIndex) setStepIndex(index);
  };

  const handleNameChange = (name: string) => setFormState((prev) => ({ ...prev, name }));

  const handleWhereChange = (nextWhere: ScheduleWizardWhereState) => {
    setFormState((prev) => {
      const nextCategoryOptions = new Set(getCategoryOptions(products, getSelectedRetailers(nextWhere)));
      const selectedStoreKeys = new Set(nextWhere.selectedStores.map(getStoreKey));
      const isSameRetailer = getLockedRetailer(nextWhere) === getLockedRetailer(prev.where);

      return {
        ...prev,
        where: nextWhere,
        what: {
          ...prev.what,
          categories: prev.what.categories.filter((category) => nextCategoryOptions.has(category)),
        },
        firm: {
          ...prev.firm,
          singleFieldServiceId: isSameRetailer ? prev.firm.singleFieldServiceId : null,
          storeAssignments: Object.fromEntries(
            Object.entries(prev.firm.storeAssignments).filter(([storeKey]) => selectedStoreKeys.has(storeKey)),
          ),
        },
      };
    });
  };

  const renderStep = () => {
    switch (currentStep.key) {
      case 'where':
        return <Step1Where stores={stores} value={formState.where} onChange={handleWhereChange} />;
      case 'what':
        return (
          <Step2What
            taskTypes={taskTypes}
            products={products}
            retailers={getSelectedRetailers(formState.where)}
            taskTypesMissingCategories={taskTypesMissingCategories}
            value={formState.what}
            onChange={(what) => setFormState((prev) => ({ ...prev, what }))}
          />
        );
      case 'frequency':
        return (
          <Step3Frequency
            value={formState.frequency}
            onChange={(frequency) => setFormState((prev) => ({ ...prev, frequency }))}
          />
        );
      case 'firm':
        return (
          <Step4Firm
            stores={stores}
            selectedStores={formState.where.selectedStores}
            eligibleFieldServices={eligibleFieldServices}
            fieldServiceCoverage={fieldServiceCoverage}
            isLoadingEligibility={isLoadingEligibility}
            hasEligibilityError={hasEligibilityError}
            name={formState.name}
            onNameChange={handleNameChange}
            lacksSharedTaskType={lacksSharedTaskType}
            value={formState.firm}
            onChange={(firm) => setFormState((prev) => ({ ...prev, firm }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full w-full flex-col bg-background">
      <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-7 py-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/schedules')}
          className="h-8 flex-shrink-0 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
          <ArrowLeft size={16} />
          {t('Schedule.Wizard.BackToList')}
        </Button>
        <h1 className="text-xl font-bold text-gray-900">{t('Schedule.Wizard.Title')}</h1>
      </div>
      <div className="shrink-0 border-b border-gray-200 bg-white px-7 py-4">
        <WizardStepper steps={WIZARD_STEPS} currentIndex={stepIndex} onStepClick={goToStep} />
      </div>

      <div ref={bodyRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 px-4 py-7 sm:px-7">{renderStep()}</div>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-7 py-4">
        <Button variant="outline" onClick={goBack} className="border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
          {t('Schedule.Wizard.Back')}
        </Button>
        <div className="flex items-center gap-3">
          {!isLastStep && (
            <Badge
              variant={eligibilityBadgeVariant}
              showDot
              label={
                isLoadingEligibility
                  ? t('Schedule.Wizard.Firm.LoadingEligible')
                  : hasEligibilityError
                  ? t('Schedule.Wizard.Firm.EligibilityError')
                  : t('Schedule.Wizard.EligibleFieldServices', { count: totalFieldServicesWithCoverage })
              }
            />
          )}
          {isLastStep ? (
            <Button
              disabled={!canAdvance || isSubmitting || eligibility.status !== 'ready' || lacksSharedTaskType}
              onClick={handleSubmit}>
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {t('Schedule.Wizard.Finish')}
            </Button>
          ) : (
            <Button disabled={!canAdvance} onClick={goNext}>
              {t('Schedule.Wizard.Next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleWizard;
